import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Collapse,
  Divider,
  Grid,
  CircularProgress,
  ListItem,
  List,
  ListItemText,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArchitectureIcon from "@mui/icons-material/Architecture";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useDropzone } from "react-dropzone";
import api from "../../utils/api";
import CustomARCForm from "./CustomARCForm";

const DEFAULT_STEPS = ["Request Info", "Details & Files", "Review & Submit"];

const UploadZone = ({ label, required, hint, getRootProps, getInputProps, isDragActive, files, onRemove }) => (
  <Grid item xs={12}>
    <Box sx={{ mb: 0.75, display: "flex", alignItems: "center", gap: 0.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 700, color: "#223142" }}>
        {label}
      </Typography>
      {required && (
        <Typography component="span" sx={{ color: "#d32f2f", fontWeight: 700, fontSize: "0.85rem" }}>
          *
        </Typography>
      )}
    </Box>
    {hint && (
      <Typography variant="caption" sx={{ color: "#8a94a6", display: "block", mb: 0.75 }}>
        {hint}
      </Typography>
    )}

    <Box
      {...getRootProps()}
      sx={{
        p: 2,
        border: files.length === 0 && required
          ? "2px dashed rgba(211,47,47,0.35)"
          : "2px dashed rgba(47,99,173,0.22)",
        borderRadius: "14px",
        textAlign: "center",
        cursor: "pointer",
        backgroundColor: isDragActive
          ? "rgba(47,99,173,0.07)"
          : files.length > 0
          ? "rgba(46,125,50,0.03)"
          : "rgba(47,99,173,0.02)",
        transition: "all 0.2s",
        "&:hover": { backgroundColor: "#f5faff", borderColor: "#1976d2" },
        mb: files.length > 0 ? 1 : 0,
      }}
    >
      <input {...getInputProps()} />
      <Box display="flex" flexDirection="column" alignItems="center">
        <CloudUploadIcon sx={{ fontSize: 32, color: files.length > 0 ? "#2e7d32" : "#1976d2", mb: 0.5 }} />
        <Typography variant="body2" sx={{ color: "#223142", fontWeight: 600 }}>
          {isDragActive ? "Drop here..." : "Drag & drop or click to select"}
        </Typography>
      </Box>
    </Box>

    {files.length > 0 && (
      <Box sx={{ border: "1px solid rgba(47,99,173,0.08)", borderRadius: "12px", p: 0.75, backgroundColor: "#fff" }}>
        {files.map((file, idx) => (
          <Box
            key={idx}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              borderBottom: idx !== files.length - 1 ? "1px solid rgba(47,99,173,0.06)" : "none",
              py: 0.4,
              px: 0.5,
            }}
          >
            <Typography
              variant="body2"
              sx={{ maxWidth: "80%", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "#223142" }}
            >
              {file.name}
            </Typography>
            <IconButton
              size="small"
              onClick={() => onRemove(idx)}
              sx={{
                border: "1px solid rgba(220,38,38,0.12)",
                borderRadius: "8px",
                color: "#dc2626",
                backgroundColor: "rgba(220,38,38,0.04)",
                "&:hover": { backgroundColor: "rgba(220,38,38,0.1)" },
              }}
            >
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        ))}
      </Box>
    )}
  </Grid>
);

const AddArchitecturalRequestSmart = ({ propertyId, accountId, onClose }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [requestTypes, setRequestTypes] = useState([]);
  const [subtypes, setSubtypes] = useState([]);
  const [showContractor, setShowContractor] = useState(false);
  const [checkedAcknowledgments, setCheckedAcknowledgments] = useState({});
  const [colors, setColors] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [schemes, setSchemes] = useState([]);
  const [filteredSchemes, setFilteredSchemes] = useState([]);
  const [multiSubtypeSelections, setMultiSubtypeSelections] = useState([]);
  const [variantColors, setVariantColors] = useState([]);
  const [showVariantTable, setShowVariantTable] = useState(false);
  const [variantSearch, setVariantSearch] = useState("");
  const [page, setPage] = useState(1);
  const [typeHasColors, setTypeHasColors] = useState(false);
  const [variantSelectedColor, setVariantSelectedColor] = useState(null);
  const [acknowledgments, setAcknowledgments] = useState([]);
  const [infoSections, setInfoSections] = useState([]);
  const [formConfig, setFormConfig] = useState(null);
  const [multiTypeEntries, setMultiTypeEntries] = useState([{ type_id:"", subtype_id:"", subtypes:[] }]);

  // Custom cascading category form state
  const [customCascade, setCustomCascade] = useState([]); // [{options: [], selected: null}, ...]

  const pageSize = 10;

  const [contractor, setContractor] = useState({
    contractor_name: "",
    phone_number: "",
    company_name: "",
    notes: "",
  });

  const [formData, setFormData] = useState(() => {
    const draft = localStorage.getItem("arch_request_draft");
    return draft
      ? JSON.parse(draft)
      : {
          description: "",
          request_type: "",
          subtype_id: "",
          color_id: "",
          property_id: propertyId || "",
          account_id: accountId || "",
          comment: "",
          acknowledged: false,
          anticipated_start_date: "",
          anticipated_completion_date: "",
          freeform_content: "",
        };
  });

  const [frontPhotos, setFrontPhotos] = useState([]);
  const [impactPhotos, setImpactPhotos] = useState([]);
  const [supportingDocs, setSupportingDocs] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    const storedUser = sessionStorage.getItem("user");
    let parsedUserId = null;

    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        parsedUserId = parsedUser?.userId ?? null;
      } catch (e) {
        console.error("Failed to parse user from sessionStorage", e);
      }
    }

    setCurrentUserId(parsedUserId);
  }, []);

  useEffect(() => {
    localStorage.setItem("arch_request_draft", JSON.stringify(formData));
  }, [formData]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      property_id: propertyId || "",
      account_id: accountId || "",
    }));
  }, [propertyId, accountId]);

  useEffect(() => {
    const fetchTypesAndSchemes = async () => {
      try {
        const [typesRes, schemesRes] = await Promise.all([
          api.get(`/architectural-request-types`),
          api.get(`/color-management`),
        ]);
        setRequestTypes(typesRes.data || []);
        setSchemes(schemesRes.data || []);
      } catch (err) {
        console.error("Failed to fetch data:", err);
      }
    };
    fetchTypesAndSchemes();
  }, []);


  useEffect(() => {
  const fetchAcknowledgments = async () => {
    try {
      const res = await api.get("/architectural-request-acknowledgments");
      setAcknowledgments(res.data || []);
    } catch (err) {
      console.error("Failed to fetch acknowledgment bullets:", err);
      setAcknowledgments([]);
    }
  };
  const fetchInfoSections = async () => {
    try {
      const res = await api.get("/architectural-request-info-sections?active=1");
      setInfoSections(res.data || []);
    } catch {
      setInfoSections([]);
    }
  };

  fetchAcknowledgments();
  fetchInfoSections();
  api.get("/ar-form-config").then(r => {
    if (r.data && Object.keys(r.data).length > 0) {
      setFormConfig(r.data);
      if (r.data.form_type === "custom") {
        api.get("/arc-custom-categories/children/root")
          .then(cr => { setCustomCascade([{ options: cr.data || [], selected: null }]); })
          .catch(() => {});
      }
    }
  }).catch(() => {});
}, []);
  const handleCascadeChange = async (levelIndex, selectedId) => {
    const updated = customCascade.slice(0, levelIndex + 1).map((lvl, i) =>
      i === levelIndex ? { ...lvl, selected: selectedId } : lvl
    );
    if (selectedId) {
      try {
        const res = await api.get(`/arc-custom-categories/children/${selectedId}`);
        if (res.data && res.data.length > 0) {
          updated.push({ options: res.data, selected: null });
        }
      } catch {}
    }
    setCustomCascade(updated);
    // Store the leaf selection as arc_category_id; also set request_type to a truthy value so validation passes
    const leafId = selectedId || null;
    setFormData(prev => ({ ...prev, arc_category_id: leafId, request_type: leafId ? "__custom__" : "" }));
  };

  const handleFormChange = async (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "request_type") {
      const typeId = Number(value);
      if (!typeId) return;

      try {
        const subtypeRes = await api.get(`/architectural-request-subtypes`, {
          params: { typeId },
        });
        setSubtypes(subtypeRes.data || []);

        const schemeRes = await api.get(`/color-options/by-type`, {
          params: { type_id: typeId },
        });

        const hasColorSchemes = schemeRes.data && schemeRes.data.length > 0;
        setTypeHasColors(hasColorSchemes);

        setFormData((prev) => ({
          ...prev,
          request_type: typeId,
          subtype_id: "",
          color_id: "",
          scheme_id: "",
        }));

        setFilteredSchemes(schemeRes.data || []);
        setColors([]);
      } catch (err) {
        console.error("Error fetching subtypes/schemes:", err);
        setSubtypes([]);
        setFilteredSchemes([]);
        setColors([]);
        setTypeHasColors(false);
      }
    }

    if (name === "scheme_id") {
      const schemeId = value && !isNaN(value) ? Number(value) : null;
      if (!schemeId) return;

      setFormData((prev) => ({
        ...prev,
        scheme_id: schemeId,
        color_id: "",
      }));

      setColors([]);
    }

    if (name === "subtype_id") {
      const selectedSubtypeId = Number(value);
      setFormData((prev) => ({ ...prev, subtype_id: selectedSubtypeId }));

      setColors([]);
      setMultiSubtypeSelections([]);
      setVariantColors([]);
      setShowVariantTable(null);

      const hasType = name === "request_type" ? Number(value) : formData.request_type;
      const hasScheme =
        name === "scheme_id" ? Number(value) : formData.scheme_id;

      if (typeHasColors && (!hasType || !hasScheme)) {
        setSnackbar({
          open: true,
          message: "Please select a Request Type and Scheme first.",
          severity: "warning",
        });
        return;
      }

      if (selectedSubtypeId === 1057) {
        try {
          const subtypeRes = await api.get(`/architectural-request-subtypes`, {
            params: { typeId: formData.request_type },
          });

          const allSubtypes = subtypeRes.data?.filter(
            (s) => s.subtype_id !== 1057
          );
          const colorData = [];

          for (const sub of allSubtypes) {
            const colorRes = await api.get(`/color-options/by-scheme`, {
              params: {
                scheme_id: Number(formData.scheme_id),
                type_id: Number(formData.request_type),
                subtype_id: sub.subtype_id,
              },
            });

            let subtypeColors = colorRes.data || [];

            if (sub.subtype_id === 1061) {
              subtypeColors = [
                ...subtypeColors,
                { id: "variant", color_name: "Variant (All Colors)" },
              ];
            }

            colorData.push({
              subtype_id: sub.subtype_id,
              subtype_name: sub.subtype_name,
              colors: subtypeColors,
              selectedColor: "",
            });
          }

          setMultiSubtypeSelections(colorData);
        } catch (err) {
          console.error("Error loading multiple subtype colors:", err);
        }
        return;
      }

      if (selectedSubtypeId === 1061) {
        try {
          const colorRes = await api.get(`/color-options/by-scheme`, {
            params: {
              scheme_id: Number(formData.scheme_id),
              type_id: Number(formData.request_type),
              subtype_id: selectedSubtypeId,
            },
          });

          const colorData = [
            ...(colorRes.data || []),
            { id: "variant", color_name: "Variant (All Colors)" },
          ];

          setColors(colorData);
        } catch (err) {
          console.error("Error loading Front Door & Shutters colors:", err);
          setColors([]);
        }
        return;
      }

      try {
        const colorRes = await api.get(`/color-options/by-scheme`, {
          params: {
            scheme_id: Number(formData.scheme_id),
            type_id: Number(formData.request_type),
            subtype_id: selectedSubtypeId,
          },
        });
        setColors(colorRes.data || []);
      } catch (err) {
        console.error("Error fetching colors for subtype:", err);
        setColors([]);
      }
    }
  };

  const handleContractorChange = (e) => {
    const { name, value } = e.target;
    setContractor((prev) => ({ ...prev, [name]: value }));
  };

  const onDropFront = useCallback((files) => setFrontPhotos((p) => [...p, ...files]), []);
  const onDropImpact = useCallback((files) => setImpactPhotos((p) => [...p, ...files]), []);
  const onDropSupporting = useCallback((files) => setSupportingDocs((p) => [...p, ...files]), []);

  const imageAccept = { "image/*": [] };
  const allDocsAccept = {
    "image/*": [],
    "application/pdf": [".pdf"],
    "application/msword": [".doc"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
  };

  const frontDropzone = useDropzone({ onDrop: onDropFront, accept: imageAccept, multiple: true });
  const impactDropzone = useDropzone({ onDrop: onDropImpact, accept: imageAccept, multiple: true });
  const supportingDropzone = useDropzone({ onDrop: onDropSupporting, accept: allDocsAccept, multiple: true });

  const nextStep = () => setActiveStep((s) => s + 1);
  const prevStep = () => setActiveStep((s) => s - 1);

  const handleSubmit = async () => {
    if (!formData.request_type) {
      setSnackbar({
        open: true,
        message: "Please select a Request Type before submitting.",
        severity: "warning",
      });
      setActiveStep(0);
      return;
    }

    if (frontPhotos.length === 0 || impactPhotos.length === 0) {
      setSnackbar({
        open: true,
        message: "A color photo of the front of the property and the impact area are both required.",
        severity: "warning",
      });
      setActiveStep(1);
      return;
    }

    const allAcknowledged =
      acknowledgments.length > 0 &&
      acknowledgments.every((a, i) => !!checkedAcknowledgments[a.id ?? i]);

    if (!allAcknowledged) {
      setSnackbar({
        open: true,
        message: "Please check all acknowledgment statements before submitting.",
        severity: "warning",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const selectedSubtype = subtypes.find(
        (s) => s.subtype_id === Number(formData.subtype_id)
      );

      const res = await api.post(`/architectural-requests`, {
        ...formData,
        // "__custom__" is a UI-only sentinel — send null so backend treats it as custom category mode
        request_type: formData.request_type === "__custom__" ? null : formData.request_type,
        acknowledged: true,
        created_by: currentUserId,
      });

      const request_id = res.data?.id;
      if (!request_id) throw new Error("Missing request_id from response");

      // Save multi-type selections if enabled
      if (formConfig?.multi_type_enabled && multiTypeEntries.some(e => e.type_id)) {
        const validEntries = multiTypeEntries.filter(e => e.type_id);
        if (validEntries.length > 0) {
          await api.post(`/architectural-requests/${request_id}/multi-types`, {
            types: validEntries.map(e => ({ type_id: e.type_id, subtype_id: e.subtype_id || null }))
          }).catch(err => console.error('Multi-type save failed:', err.message));
        }
      }

      if (formData.scheme_id) {
        try {
          if (formData.subtype_id === 1057 && multiSubtypeSelections.length > 0) {
            for (const item of multiSubtypeSelections) {
              if (!item.selectedColor) continue;

              let colorOptionId = item.selectedColor;
              if (item.subtype_id === 1061 && colorOptionId === "variant") {
                continue;
              }

              await api.post(`/architectural-request-color-selections`, {
                architectural_request_id: request_id,
                scheme_id: formData.scheme_id,
                color_option_id: colorOptionId,
                type_id: formData.request_type,
                subtype_id: item.subtype_id,
              });
            }
          } else if (formData.color_id) {
            await api.post(`/architectural-request-color-selections`, {
              architectural_request_id: request_id,
              scheme_id: formData.scheme_id,
              color_option_id: formData.color_id,
              type_id: formData.request_type,
              subtype_id: formData.subtype_id,
            });
          }
        } catch (colorErr) {
          console.warn("Failed to record color selection(s):", colorErr);
        }
      }

      const allFiles = [...frontPhotos, ...impactPhotos, ...supportingDocs];
      if (allFiles.length > 0) {
        const uploadData = new FormData();
        uploadData.append("request_id", request_id);
        allFiles.forEach((file) => uploadData.append("upload_urls", file));

        await api.post(`/request-files`, uploadData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

     const hasContractorData = Object.values(contractor).some(
  (value) => String(value || "").trim() !== ""
);

if (showContractor && hasContractorData) {
  await api.post(`/architectural-request-contractors`, {
    architectural_request_id: request_id,
    contractor_name: contractor.contractor_name?.trim() || "",
    phone_number: contractor.phone_number?.trim() || "",
    company_name: contractor.company_name?.trim() || "",
    notes: contractor.notes?.trim() || "",
  });
}

      if (selectedSubtype?.requires_freeform && formData.freeform_content) {
        await api.post(`/architectural-request-freeform-responses`, {
          request_id,
          type_id: formData.request_type,
          subtype_id: formData.subtype_id,
          field_content: formData.freeform_content,
        });
      }

      setFormData({
        description: "",
        request_type: "",
        subtype_id: "",
        scheme_id: "",
        color_id: "",
        property_id: propertyId,
        account_id: accountId,
        comment: "",
        acknowledged: false,
        anticipated_start_date: "",
        anticipated_completion_date: "",
        freeform_content: "",
      });

      setContractor({
        contractor_name: "",
        phone_number: "",
        company_name: "",
        notes: "",
      });

      setFrontPhotos([]);
      setImpactPhotos([]);
      setSupportingDocs([]);
      setCheckedAcknowledgments({});
      setShowContractor(false);
      setMultiSubtypeSelections([]);
      setVariantColors([]);
      setFilteredSchemes([]);
      setColors([]);
      setShowVariantTable(false);
      setVariantSearch("");
      setPage(1);
      setActiveStep(0);
      setTypeHasColors(false);
      setVariantSelectedColor(null);
      localStorage.removeItem("arch_request_draft");

      setSnackbar({
        open: true,
        message: "✅ Architectural Request submitted successfully!",
        severity: "success",
      });

      setTimeout(() => {
        if (onClose) onClose();
      }, 2000);
    } catch (err) {
      console.error("Submit Error:", err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "Submission failed",
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const sectionCardSx = {
    ...pageCardSx,
    p: { xs: 1.5, sm: 2, md: 2.5 },
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const readonlyFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#f7f9fc",
      borderRadius: "10px",
    },
  };

  const selectSx = {
    borderRadius: "10px",
    backgroundColor: "rgba(255,255,255,0.96)",
    "& .MuiSelect-select": {
      py: 1.7,
    },
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.5,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2.25,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  const renderVariantTable = (onSelectColor, selectedId) => {
    if (!variantColors.length) return null;

    const filtered = variantColors.filter((color) =>
      color.color_name.toLowerCase().includes(variantSearch.toLowerCase())
    );
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    const paged = filtered.slice(start, end);
    const totalPages = Math.ceil(filtered.length / pageSize);

    return (
      <Box sx={{ mt: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Search colors..."
          value={variantSearch}
          onChange={(e) => {
            setVariantSearch(e.target.value);
            setPage(1);
          }}
          sx={{ ...textFieldSx, mb: 2 }}
        />

        <Box
          component="table"
          sx={{
            width: "100%",
            borderCollapse: "collapse",
            backgroundColor: "#fafafa",
            borderRadius: "14px",
            overflow: "hidden",
            "& th, & td": {
              border: "1px solid #e7ebf0",
              padding: "10px 12px",
              textAlign: "left",
            },
            "& th": {
              backgroundColor: "#f7f9fc",
              fontWeight: 700,
              color: "#223142",
            },
            "& tr:hover": {
              backgroundColor: "#eef5ff",
              cursor: "pointer",
            },
          }}
        >
          <thead>
            <tr>
              <th>Color Name</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {paged.length > 0 ? (
              paged.map((color) => (
                <tr
                  key={color.id}
                  onClick={() => onSelectColor(color)}
                  style={{
                    backgroundColor:
                      selectedId === color.id ? "#e8f4ff" : "transparent",
                  }}
                >
                  <td>{color.color_name}</td>
                  <td>{color.description || "-"}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={2}
                  style={{
                    textAlign: "center",
                    padding: "16px",
                    color: "#777",
                  }}
                >
                  No colors found.
                </td>
              </tr>
            )}
          </tbody>
        </Box>

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mt={2}
        >
          <Button
            size="small"
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            sx={secondaryButtonSx}
          >
            Previous
          </Button>
          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            Page {page} of {totalPages || 1}
          </Typography>
          <Button
            size="small"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            sx={secondaryButtonSx}
          >
            Next
          </Button>
        </Box>
      </Box>
    );
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Card sx={sectionCardSx}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
              {formConfig?.step_headings?.[0] || "Request Information"}
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c", mb: 2.5 }}>
              Select the request type, subtype, and any required color or scheme details.
            </Typography>

            {/* Dynamic info sections managed by admin */}
            {infoSections.map((sec) => (
              <Box key={sec.id} sx={{ p: 2.25, mb: 2, borderRadius: "14px", backgroundColor: sec.bg_color, border: `1px solid ${sec.border_color}` }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#223142", mb: 1 }}>{sec.title}</Typography>
                {sec.content_type === "bullets" ? (
                  <Box component="ul" sx={{ pl: 2.5, m: 0, "& li": { color: "#5f6f86", fontSize: "0.875rem", lineHeight: 1.7 } }}>
                    {sec.content.split("\n").filter(l => l.trim()).map((line, i) => <li key={i}>{line.trim()}</li>)}
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: "#6b778c" }}>{sec.content}</Typography>
                )}
              </Box>
            ))}

            <Grid container spacing={2.5}>
              {formConfig?.form_type === "custom" ? (
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#223142", mb: 1.5 }}>
                    Category
                    <Typography component="span" sx={{ color: "#d32f2f", ml: 0.5 }}>*</Typography>
                  </Typography>
                  {customCascade.map((level, idx) => (
                    <FormControl fullWidth key={idx} sx={{ mb: 1.5 }}>
                      <InputLabel>
                        {idx === 0 ? "Category" : `Sub-category${idx > 1 ? ` (level ${idx + 1})` : ""}`}
                      </InputLabel>
                      <Select
                        value={level.selected || ""}
                        label={idx === 0 ? "Category" : `Sub-category${idx > 1 ? ` (level ${idx + 1})` : ""}`}
                        onChange={e => handleCascadeChange(idx, e.target.value || null)}
                        sx={selectSx}
                      >
                        <MenuItem value=""><em>Select…</em></MenuItem>
                        {level.options.map(opt => (
                          <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ))}
                </Grid>
              ) : formConfig?.multi_type_enabled ? (
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ fontWeight:700, color:"#223142", mb:1 }}>
                    {formConfig?.field_labels?.request_type || "Request Type(s)"}
                    <Typography component="span" sx={{ color:"#d32f2f", ml:0.5 }}>*</Typography>
                  </Typography>
                  {multiTypeEntries.map((entry, idx) => (
                    <Box key={idx} sx={{ display:"flex", gap:1, mb:1, alignItems:"flex-start",
                      p:1.5, borderRadius:"10px", bgcolor:"rgba(47,99,173,0.03)", border:"1px solid rgba(47,99,173,0.1)" }}>
                      <Box sx={{ flex:1 }}>
                        <FormControl fullWidth size="small" sx={{ mb:1 }}>
                          <InputLabel>{formConfig?.field_labels?.request_type || "Request Type"}</InputLabel>
                          <Select value={entry.type_id} label={formConfig?.field_labels?.request_type || "Request Type"}
                            onChange={async (e) => {
                              const tid = e.target.value;
                              let subs = [];
                              if (tid) {
                                try { const r = await api.get(`/architectural-request-subtypes?typeId=${tid}`); subs = r.data || []; } catch {}
                              }
                              setMultiTypeEntries(prev => prev.map((en,i) => i===idx ? { ...en, type_id:tid, subtype_id:"", subtypes:subs } : en));
                              if (idx === 0) setFormData(p => ({ ...p, request_type: tid }));
                            }}>
                            {requestTypes.map(t => <MenuItem key={t.id} value={t.id}>{t.type}</MenuItem>)}
                          </Select>
                        </FormControl>
                        {entry.subtypes?.length > 0 && (
                          <FormControl fullWidth size="small">
                            <InputLabel>{formConfig?.field_labels?.subtype || "Subtype"}</InputLabel>
                            <Select value={entry.subtype_id} label={formConfig?.field_labels?.subtype || "Subtype"}
                              onChange={e => { const sid=e.target.value; setMultiTypeEntries(prev => prev.map((en,i) => i===idx ? {...en,subtype_id:sid} : en)); if(idx===0) setFormData(p=>({...p,subtype_id:sid})); }}>
                              <MenuItem value="">None</MenuItem>
                              {entry.subtypes.map(s => <MenuItem key={s.subtype_id} value={s.subtype_id}>{s.subtype_name}</MenuItem>)}
                            </Select>
                          </FormControl>
                        )}
                      </Box>
                      {multiTypeEntries.length > 1 && (
                        <IconButton size="small" color="error" onClick={() => setMultiTypeEntries(prev => prev.filter((_,i)=>i!==idx))}
                          sx={{ mt:0.5 }}><DeleteIcon fontSize="small" /></IconButton>
                      )}
                    </Box>
                  ))}
                  <Button size="small" startIcon={<AddCircleOutlineIcon />}
                    onClick={() => setMultiTypeEntries(prev => [...prev, { type_id:"", subtype_id:"", subtypes:[] }])}
                    sx={{ borderRadius:"8px", textTransform:"none", color:"#2f63ad", border:"1px dashed rgba(47,99,173,0.3)", px:1.5 }}>
                    Add Another Type
                  </Button>
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>{formConfig?.field_labels?.request_type || "Request Type"}</InputLabel>
                    <Select name="request_type" value={formData.request_type} onChange={handleFormChange} sx={selectSx}
                      label={formConfig?.field_labels?.request_type || "Request Type"}>
                      {requestTypes.map((type) => <MenuItem key={type.id} value={type.id}>{type.type}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Property ID"
                  value={propertyId || "N/A"}
                  InputProps={{ readOnly: true }}
                  sx={readonlyFieldSx}
                />
              </Grid>

              {filteredSchemes.length > 0 && (formConfig?.fields?.scheme ?? true) && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Scheme</InputLabel>
                    <Select
                      name="scheme_id"
                      value={formData.scheme_id || ""}
                      onChange={handleFormChange}
                      sx={selectSx}
                    >
                      {filteredSchemes.map((scheme) => (
                        <MenuItem key={scheme.scheme_id} value={Number(scheme.scheme_id)}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box
                              sx={{
                                width: 18,
                                height: 18,
                                borderRadius: "4px",
                                backgroundColor: scheme.hex_value || "#ccc",
                                border: "1px solid #aaa",
                              }}
                            />
                            <Typography>{scheme.color_name}</Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {(formConfig?.fields?.subtype ?? true) && !formConfig?.multi_type_enabled && (
                <Grid item xs={12}>
                  <FormControl fullWidth disabled={!subtypes.length}>
                    <InputLabel>Subtype</InputLabel>
                    <Select name="subtype_id" value={formData.subtype_id} onChange={handleFormChange} sx={selectSx}>
                      {subtypes.map((sub) => (
                        <MenuItem key={sub.subtype_id} value={sub.subtype_id}>{sub.subtype_name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {(() => {
                const selectedSubtype = subtypes.find(
                  (s) => s.subtype_id === Number(formData.subtype_id)
                );

                if (selectedSubtype?.requires_freeform) {
                  return (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Additional Details"
                        name="freeform_content"
                        value={formData.freeform_content || ""}
                        onChange={handleFormChange}
                        multiline
                        rows={3}
                        sx={textFieldSx}
                        placeholder="Enter any additional details required for this subtype..."
                      />
                    </Grid>
                  );
                }
                return null;
              })()}

              {colors.length > 0 && (formConfig?.fields?.color ?? true) && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Color</InputLabel>
                    <Select
                      name="color_id"
                      value={formData.color_id || ""}
                      onChange={async (e) => {
                        const value = e.target.value;
                        setFormData((prev) => ({ ...prev, color_id: value }));

                        if (formData.subtype_id === 1061 && value === "variant") {
                          try {
                            const allColorRes = await api.get(`/color-options`);
                            setVariantColors(allColorRes.data || []);
                            setShowVariantTable(true);
                          } catch (err) {
                            console.error("Failed to fetch all color options:", err);
                            setVariantColors([]);
                            setShowVariantTable(false);
                          }
                        } else {
                          setShowVariantTable(false);
                        }
                      }}
                      renderValue={(selected) => {
                        if (!selected) return <Typography>Select Color</Typography>;

                        const color = colors.find((c) => c.id === selected);

                        if (selected === "variant" && formData.variantSelectedColor) {
                          const chosenVariant = variantColors.find(
                            (v) => v.id === formData.variantSelectedColor
                          );
                          if (chosenVariant) {
                            return (
                              <Box display="flex" alignItems="center" gap={1}>
                                <Box
                                  sx={{
                                    width: 18,
                                    height: 18,
                                    borderRadius: "4px",
                                    backgroundColor: chosenVariant.hex_value || "#ccc",
                                    border: "1px solid #bbb",
                                  }}
                                />
                                <Typography>{`Variant (${chosenVariant.color_name})`}</Typography>
                              </Box>
                            );
                          }
                        }

                        if (selected === "variant") {
                          return (
                            <Typography sx={{ fontStyle: "italic" }}>
                              Variant (All Colors)
                            </Typography>
                          );
                        }

                        return color ? (
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box
                              sx={{
                                width: 18,
                                height: 18,
                                borderRadius: "4px",
                                backgroundColor: color.hex_value || "#ccc",
                                border: "1px solid #bbb",
                              }}
                            />
                            <Typography>{color.color_name}</Typography>
                          </Box>
                        ) : (
                          <Typography>Select Color</Typography>
                        );
                      }}
                      sx={selectSx}
                    >
                      {colors.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box
                              sx={{
                                width: 22,
                                height: 22,
                                borderRadius: "4px",
                                backgroundColor: c.hex_value || "#ccc",
                                border: "1px solid #aaa",
                              }}
                            />
                            <Typography fontSize="0.9rem" fontWeight="500">
                              {c.color_name}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>

                    {showVariantTable && variantColors.length > 0 && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700, color: "#223142" }}>
                          Select a Variant Color from All Schemes
                        </Typography>

                        {renderVariantTable(
                          (color) => {
                            setColors((prev) => {
                              const exists = prev.some((c) => c.id === color.id);
                              if (exists) return prev;
                              return [
                                ...prev,
                                {
                                  id: color.id,
                                  color_name: color.color_name,
                                  description: color.description,
                                },
                              ];
                            });

                            setFormData((prev) => ({
                              ...prev,
                              color_id: color.id,
                              variantSelectedColor: color.id,
                            }));
                            setVariantSelectedColor(color.id);
                          },
                          formData.color_id
                        )}
                      </Box>
                    )}
                  </FormControl>
                </Grid>
              )}

              {multiSubtypeSelections.map((item, i) => (
                <Grid item xs={12} key={i}>
                  <FormControl fullWidth>
                    <InputLabel>{item.subtype_name}</InputLabel>
                    <Select
                      value={item.selectedColor || ""}
                      onChange={async (e) => {
                        const value = e.target.value;
                        const updated = [...multiSubtypeSelections];
                        updated[i].selectedColor = value;
                        setMultiSubtypeSelections(updated);

                        if (item.subtype_id === 1061 && value === "variant") {
                          try {
                            const allColorRes = await api.get(`/color-options`);
                            setVariantColors(allColorRes.data || []);
                            setShowVariantTable(i);
                          } catch (err) {
                            console.error("Failed to load all variant colors:", err);
                            setVariantColors([]);
                            setShowVariantTable(null);
                          }
                        } else {
                          setShowVariantTable(null);
                        }
                      }}
                      renderValue={(selected) => {
                        if (!selected) return <Typography>Select Color</Typography>;

                        const color = item.colors.find((c) => c.id === selected);

                        return color ? (
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: "4px",
                                backgroundColor: color.hex_value || "#ccc",
                                border: "1px solid #bbb",
                              }}
                            />
                            <Typography>{color.color_name}</Typography>
                          </Box>
                        ) : (
                          <Typography>Select Color</Typography>
                        );
                      }}
                      sx={selectSx}
                    >
                      {item.colors.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: "4px",
                                backgroundColor: c.hex_value || "#ccc",
                                border: "1px solid #aaa",
                              }}
                            />
                            <Typography>{c.color_name}</Typography>
                          </Box>
                        </MenuItem>
                      ))}

                      {showVariantTable === i &&
                        variantColors.map((color) => {
                          const exists = item.colors.some((c) => c.id === color.id);
                          if (!exists && item.selectedColor === color.id) {
                            return (
                              <MenuItem key={color.id} value={color.id}>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Box
                                    sx={{
                                      width: 20,
                                      height: 20,
                                      borderRadius: "4px",
                                      backgroundColor: color.hex_value || "#ccc",
                                      border: "1px solid #aaa",
                                    }}
                                  />
                                  <Typography>{color.color_name}</Typography>
                                </Box>
                              </MenuItem>
                            );
                          }
                          return null;
                        })}
                    </Select>
                  </FormControl>

                  {showVariantTable === i && variantColors.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700, color: "#223142" }}>
                        Select a Variant Color from All Schemes
                      </Typography>

                      {renderVariantTable(
                        (color) => {
                          const updated = [...multiSubtypeSelections];
                          updated[i].selectedColor = color.id;
                          setMultiSubtypeSelections(updated);
                          setShowVariantTable(false);
                        },
                        item.selectedColor
                      )}
                    </Box>
                  )}
                </Grid>
              ))}
            </Grid>
          </Card>
        );

      case 1:
        return (
          <Card sx={sectionCardSx}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
              {formConfig?.step_headings?.[1] || "Details & Files"}
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c", mb: 2.5 }}>
              Add request details, timeline, supporting files, and optional contractor information.
            </Typography>

            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <Tooltip title="Provide a detailed explanation of your request">
                  <TextField
                    fullWidth
                    label={formConfig?.field_labels?.description || "Description"}
                    name="description"
                    value={formData.description}
                    onChange={handleFormChange}
                    multiline
                    rows={3}
                    sx={textFieldSx}
                    required
                  />
                </Tooltip>
              </Grid>

              {(formConfig?.fields?.comment ?? true) && (
                <Grid item xs={12}>
                  <TextField fullWidth label={formConfig?.field_labels?.comment || "Comment"} name="comment"
                    value={formData.comment} onChange={handleFormChange} multiline rows={2} sx={textFieldSx} />
                </Grid>
              )}

              {(formConfig?.fields?.start_date ?? true) && (
                <Grid item xs={12} md={6}>
                  <TextField fullWidth type="date" label={formConfig?.field_labels?.start_date || "Anticipated Start Date"}
                    name="anticipated_start_date" InputLabelProps={{ shrink: true }}
                    value={formData.anticipated_start_date || ""} onChange={handleFormChange} sx={textFieldSx} />
                </Grid>
              )}

              {(formConfig?.fields?.end_date ?? true) && (
                <Grid item xs={12} md={6}>
                  <TextField fullWidth type="date" label={formConfig?.field_labels?.end_date || "Anticipated Completion Date"}
                    name="anticipated_completion_date" InputLabelProps={{ shrink: true }}
                    value={formData.anticipated_completion_date || ""} onChange={handleFormChange} sx={textFieldSx} />
                </Grid>
              )}

              {(formConfig?.fields?.photo_front ?? true) && (
                <UploadZone label={formConfig?.field_labels?.photo_front || "Color Photo of Front of Property"} required
                  hint="Upload at least one clear color photo of the front of the property."
                  getRootProps={frontDropzone.getRootProps} getInputProps={frontDropzone.getInputProps}
                  isDragActive={frontDropzone.isDragActive} files={frontPhotos}
                  onRemove={(i) => setFrontPhotos((p) => p.filter((_, idx) => idx !== i))} />
              )}
              {(formConfig?.fields?.photo_impact ?? true) && (
                <UploadZone label={formConfig?.field_labels?.photo_impact || "Color Photo of Impact Area"} required
                  hint="Upload at least one color photo of the area that will be impacted."
                  getRootProps={impactDropzone.getRootProps} getInputProps={impactDropzone.getInputProps}
                  isDragActive={impactDropzone.isDragActive} files={impactPhotos}
                  onRemove={(i) => setImpactPhotos((p) => p.filter((_, idx) => idx !== i))} />
              )}
              {(formConfig?.fields?.documents ?? true) && (
                <UploadZone label={formConfig?.field_labels?.documents || "Supporting Documents"}
                  hint="Plat Map/Survey, landscaping plans, photos of materials, permits, etc. (optional)"
                  getRootProps={supportingDropzone.getRootProps} getInputProps={supportingDropzone.getInputProps}
                  isDragActive={supportingDropzone.isDragActive} files={supportingDocs}
                  onRemove={(i) => setSupportingDocs((p) => p.filter((_, idx) => idx !== i))} />
              )}

              {(formConfig?.fields?.contractor ?? true) && (
                <Grid item xs={12}>
                  <Button startIcon={<AddCircleOutlineIcon />}
                    onClick={() => setShowContractor((prev) => !prev)}
                    variant="outlined" sx={secondaryButtonSx}>
                    {showContractor ? "Hide Contractor Info" : "Add Contractor Info"}
                  </Button>
                </Grid>
              )}

              {(formConfig?.fields?.contractor ?? true) && (
                <Grid item xs={12}>
                  <Collapse in={showContractor}>
                  <Box
                    sx={{
                      p: 2,
                      border: "1px solid rgba(47,99,173,0.08)",
                      borderRadius: "14px",
                      backgroundColor: "rgba(47,99,173,0.03)",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      sx={{ fontWeight: 700, color: "#223142", mb: 1.5 }}
                    >
                      Contractor Information
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          name="contractor_name"
                          label="Name"
                          value={contractor.contractor_name}
                          onChange={handleContractorChange}
                          sx={textFieldSx}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          name="phone_number"
                          label="Phone"
                          value={contractor.phone_number}
                          onChange={handleContractorChange}
                          sx={textFieldSx}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          name="company_name"
                          label="Company"
                          value={contractor.company_name}
                          onChange={handleContractorChange}
                          sx={textFieldSx}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          name="notes"
                          label={formConfig?.field_labels?.notes || "Notes"}
                          value={contractor.notes}
                          onChange={handleContractorChange}
                          multiline
                          rows={2}
                          sx={textFieldSx}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Collapse>
              </Grid>
              )}
            </Grid>
          </Card>
        );

      case 2:
        return (
          <Card sx={sectionCardSx}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <CheckCircleOutlineIcon sx={{ color: "#2f63ad" }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
                {formConfig?.step_headings?.[2] || "Review & Acknowledge"}
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ color: "#6b778c", mb: 2.5 }}>
              Confirm the request details and acknowledge the certification before submitting.
            </Typography>

            <Divider sx={{ mb: 2.5, borderColor: "rgba(47,99,173,0.08)" }} />

            <Grid container spacing={2.5}>
              {formConfig?.form_type === "custom" ? (
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ color: "#6b778c" }}>
                    Category Selection
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#223142" }}>
                    {customCascade
                      .filter(lvl => lvl.selected)
                      .map(lvl => lvl.options.find(o => o.id === lvl.selected)?.name)
                      .filter(Boolean)
                      .join(" › ") || "N/A"}
                  </Typography>
                </Grid>
              ) : (
                <>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#6b778c" }}>
                      Type
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#223142" }}>
                      {requestTypes.find((t) => t.id === formData.request_type)?.type || "N/A"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#6b778c" }}>
                      Subtype
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#223142" }}>
                      {subtypes.find((s) => s.subtype_id === formData.subtype_id)?.subtype_name || "N/A"}
                    </Typography>
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: "#6b778c" }}>
                  Description
                </Typography>
                <Typography variant="subtitle1" sx={{ color: "#223142" }}>
                  {formData.description || "N/A"}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: "#6b778c" }}>
                  Files
                </Typography>
                {[
                  ...frontPhotos.map((f) => ({ name: f.name, label: "Front" })),
                  ...impactPhotos.map((f) => ({ name: f.name, label: "Impact" })),
                  ...supportingDocs.map((f) => ({ name: f.name, label: "Supporting" })),
                ].length > 0 ? (
                  <Box sx={{ mt: 0.5 }}>
                    {[
                      ...frontPhotos.map((f) => ({ name: f.name, label: "Front" })),
                      ...impactPhotos.map((f) => ({ name: f.name, label: "Impact" })),
                      ...supportingDocs.map((f) => ({ name: f.name, label: "Supporting" })),
                    ].map((f, i) => (
                      <Typography key={i} variant="body2" sx={{ color: "#223142" }}>
                        <Box component="span" sx={{ fontWeight: 700, color: "#2f63ad", mr: 0.5 }}>
                          [{f.label}]
                        </Box>
                        {f.name}
                      </Typography>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="subtitle1" sx={{ color: "#d32f2f" }}>
                    No files attached. front photo and impact area photo are required.
                  </Typography>
                )}
              </Grid>
            </Grid>

            <Box
              sx={{
                p: 2.5,
                mt: 3,
                borderRadius: "16px",
                backgroundColor: "#f5f7fb",
                border: "1px solid #d9e1ec",
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
                Acknowledgment Statements
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
                Please read and check each statement below before submitting.
              </Typography>

              {acknowledgments.length > 0 ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {acknowledgments.map((item, idx) => {
                    const key = item.id ?? idx;
                    const checked = !!checkedAcknowledgments[key];
                    return (
                      <Box
                        key={key}
                        onClick={() =>
                          setCheckedAcknowledgments((prev) => ({ ...prev, [key]: !prev[key] }))
                        }
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1.25,
                          p: 1.5,
                          borderRadius: "12px",
                          border: checked
                            ? "1px solid rgba(46,125,50,0.3)"
                            : "1px solid rgba(47,99,173,0.12)",
                          backgroundColor: checked
                            ? "rgba(46,125,50,0.04)"
                            : "rgba(255,255,255,0.8)",
                          cursor: "pointer",
                          transition: "all 0.15s",
                          "&:hover": {
                            backgroundColor: checked
                              ? "rgba(46,125,50,0.07)"
                              : "rgba(47,99,173,0.04)",
                          },
                        }}
                      >
                        <Checkbox
                          checked={checked}
                          onChange={() =>
                            setCheckedAcknowledgments((prev) => ({ ...prev, [key]: !prev[key] }))
                          }
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            p: 0,
                            mt: "1px",
                            flexShrink: 0,
                            color: checked ? "#2e7d32" : "#9aa6b2",
                            "&.Mui-checked": { color: "#2e7d32" },
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: checked ? "#1b5e20" : "#5f6f86",
                            lineHeight: 1.6,
                            fontWeight: checked ? 600 : 400,
                          }}
                        >
                          {item.bullet_text}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: "#8a94a6" }}>
                  No acknowledgment statements found.
                </Typography>
              )}
            </Box>
          </Card>
        );

      default:
        return null;
    }
  };

  const allAcknowledged =
    acknowledgments.length > 0 &&
    acknowledgments.every((a, i) => !!checkedAcknowledgments[a.id ?? i]);

  const nextDisabled =
    isSubmitting ||
    (activeStep === 0 && !formData.request_type) ||
    (activeStep === 1 &&
      (!formData.description.trim() ||
        frontPhotos.length === 0 ||
        impactPhotos.length === 0));

  // ── Custom form — render dedicated multi-category wizard ──────────────────
  if (formConfig?.form_type === "custom") {
    return (
      <Card sx={{ ...pageCardSx, overflow: "hidden" }}>
        <Box sx={{ px: { xs: 1.5, sm: 2, md: 3 }, pt: 2.25, pb: 1.5, borderBottom: "1px solid rgba(47,99,173,0.08)", bgcolor: "rgba(47,99,173,0.02)" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25 }}>
            <ArchitectureIcon sx={{ color: "#2f63ad" }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>Add Architectural Request</Typography>
          </Box>
          <Typography variant="body2" sx={{ color: "#6b778c" }}>Submit one or more category requests with photos and details.</Typography>
        </Box>
        <CustomARCForm
          propertyId={propertyId}
          accountId={accountId}
          onClose={onClose}
          acknowledgments={acknowledgments}
          infoSections={infoSections}
        />
      </Card>
    );
  }

  return (
    <Card sx={{ ...pageCardSx, overflow: "hidden" }}>
      <Box
        sx={{
          px: { xs: 1.5, sm: 2, md: 3 },
          pt: 2.25,
          pb: 1.5,
          borderBottom: "1px solid rgba(47,99,173,0.08)",
          backgroundColor: "rgba(47,99,173,0.02)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <ArchitectureIcon sx={{ color: "#2f63ad" }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
            {formConfig?.title || "Add Architectural Request"}
          </Typography>
        </Box>

        <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
          Create a new architectural request and upload the required supporting documentation.
        </Typography>

        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{
            "& .MuiStepLabel-label": {
              fontSize: "0.9rem",
            },
            "& .MuiStepLabel-label.Mui-active": {
              fontWeight: 700,
              color: "#223142",
            },
            "& .MuiStepLabel-label.Mui-completed": {
              fontWeight: 600,
            },
          }}
        >
          {(formConfig?.step_labels || DEFAULT_STEPS).map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        {renderStepContent()}

        <Divider sx={{ my: 3, borderColor: "rgba(47,99,173,0.08)" }} />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={prevStep}
            disabled={activeStep === 0 || isSubmitting}
            sx={secondaryButtonSx}
          >
            Back
          </Button>

          {activeStep < (formConfig?.step_labels || DEFAULT_STEPS).length - 1 ? (
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={nextStep}
              disabled={nextDisabled}
              sx={primaryButtonSx}
            >
              Next
            </Button>
          ) : (
            <Tooltip
              title={
                !allAcknowledged
                  ? "Please check all acknowledgment statements before submitting"
                  : ""
              }
            >
              <span>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={!allAcknowledged || isSubmitting}
                  sx={primaryButtonSx}
                >
                  {isSubmitting ? (
                    <CircularProgress size={22} color="inherit" />
                  ) : (
                    formConfig?.submit_label || "Submit Request"
                  )}
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 3 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default AddArchitecturalRequestSmart;
